/**
 * PlacementHistorie - the Tab-6 read-only end-to-end timeline (P2 Placement Slice 15). Phase chips
 * (Alle + the phases that have entries), a «Technische Eintraege anzeigen» toggle (switches the quelle),
 * the shared Timeline, and «Mehr laden» on the next cursor.
 */
import { useMemo, useState } from 'react';
import { PK_HISTORIE_LEER, PK_HISTORIE_TECHNISCH_LABEL, PLACEMENT_HISTORIE_PHASE_LABELS, type PlacementHistoriePhase } from '@brokly/shared';
import { Timeline, type TimelineEvent } from '../entity-detail/components/Timeline';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Button } from '../../components/ui/Button';
import { dateTimeShortDe } from '../../lib/format';
import { usePlacementHistorie } from './usePlacementHistorie';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';

export function PlacementHistorie({ placementId }: { placementId: string }) {
  const { can } = useEffectivePermissions();
  const canAudit = can('audit.read');
  const [technisch, setTechnisch] = useState(false);
  const [phase, setPhase] = useState<'alle' | PlacementHistoriePhase>('alle');
  // #11: the audit leg needs audit.read; without it the toggle is hidden and the API degrades to activity-only.
  const quelle = technisch && canAudit ? 'alle' : 'aktivitaet';
  const query = usePlacementHistorie(placementId, quelle);

  const eintraege = useMemo(() => (query.data?.pages ?? []).flatMap((p) => p.eintraege), [query.data]);
  const phasesPresent = useMemo(() => Array.from(new Set(eintraege.map((e) => e.phase))), [eintraege]);

  const filtered = phase === 'alle' ? eintraege : eintraege.filter((e) => e.phase === phase);
  const events: TimelineEvent[] = filtered.map((e) => {
    const kontextLine = Object.entries(e.kontext).map(([k, v]) => `${k}=${String(v)}`).join(' · ');
    const base = e.text || (e.action ? `${e.action}${e.entity_type ? ` · ${e.entity_type}` : ''}` : '');
    const actor = e.actor_kind === 'system' ? 'System' : e.actor_name ?? '';
    return {
      id: e.id,
      datum: dateTimeShortDe(e.zeitpunkt),
      text: (
        <span>
          {base}
          {actor ? <span className="ml-1 text-xs text-muted-foreground">({actor})</span> : null}
          {e.quelle === 'audit' && kontextLine ? <span className="block text-xs text-muted-foreground">{kontextLine}</span> : null}
        </span>
      ),
    };
  });

  const phaseOptions = [
    { value: 'alle', label: 'Alle' },
    ...phasesPresent.map((p) => ({ value: p, label: PLACEMENT_HISTORIE_PHASE_LABELS[p] })),
  ].slice(0, 11);

  return (
    <div className="space-y-3" data-testid="placement-historie">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {phaseOptions.length > 1 && (
          <SegmentedControl ariaLabel="Phase filtern" value={phase} onValueChange={(v) => setPhase(v as 'alle' | PlacementHistoriePhase)} options={phaseOptions.slice(0, 4)} />
        )}
        {canAudit && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={technisch} onChange={(e) => setTechnisch(e.target.checked)} data-testid="placement-historie-technisch" />
            {PK_HISTORIE_TECHNISCH_LABEL}
          </label>
        )}
      </div>
      <Timeline events={events} empty={PK_HISTORIE_LEER} />
      {query.hasNextPage && (
        <div className="flex justify-center">
          <Button size="sm" variant="ghost" disabled={query.isFetchingNextPage} onClick={() => query.fetchNextPage()} data-testid="placement-historie-mehr">
            Mehr laden
          </Button>
        </div>
      )}
    </div>
  );
}
